import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import { debounce } from "lodash";

export default function BadgeManagement() {
    // State management
    const [state, setState] = useState({
        badges: [],
        loading: true,
        formSubmitting: false,
        formData: {
            badgesId: 0,
            badgesName: "",
            badgesCategory: "",
            badgesCriteriaEarning: "",
            badgesPoint: 0,
            badgeStatus: true,
        },
        searchQuery: "",
        filterStatus: "all",
        currentPage: 0,
        itemsPerPage: 5,
    });

    // Helper function to update state
    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchBadges();
    }, []);

    // API calls
    const fetchBadges = async () => {
        updateState({ loading: true });
        try {
            const response = await axios.get(
                `${API_LoginURL}badges/BadgesMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                badges: Array.isArray(response.data) ? response.data : [],
                loading: false,
            });
        } catch (error) {
            console.error("Error fetching badges:", error);
            Swal.fire("Error!", "Failed to load badges.", "error");
            updateState({ loading: false });
        }
    };

    // Helper function for headers
    const getHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateState({
            formData: {
                ...state.formData,
                [name]: value,
            },
        });
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                updateState({ searchQuery: query, currentPage: 0 });
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        updateState({
            filterStatus: e.target.value,
            currentPage: 0,
        });
    };

    const resetForm = () => {
        updateState({
            formData: {
                badgesId: 0,
                badgesName: "",
                badgesCategory: "",
                badgesCriteriaEarning: "",
                badgesPoint: 0,
                badgeStatus: true,
            },
        });
    };

    const handleEdit = (badge) => {
        updateState({
            formData: {
                ...badge,
                badgeStatus: badge.badgeStatus !== false, // Ensure boolean
            },
        });
    };

    const handleDelete = async (badgesId) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}badges/DeleteBadge`,
                { badgesId },
                { headers: getHeaders() }
            );
            updateState({
                badges: state.badges.filter(
                    (item) => item.badgesId !== badgesId
                ),
            });
            Swal.fire("Deleted!", "Badge has been deleted.", "success");
        } catch (error) {
            console.error("Error deleting badge:", error);
            Swal.fire("Error", "Failed to delete badge", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        updateState({ currentPage: selected });
    };

    const handleFormSubmit = async () => {
        updateState({ formSubmitting: true });
        try {
            const response = await axios.post(
                `${API_LoginURL}badges/BadgesAddEdit`,
                state.formData,
                { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }}
            );

            Swal.fire({
                icon:
                    response.data.response === "SUCCESS" ? "success" : "error",
                title:
                    response.data.response === "SUCCESS" ? "Success" : "Error",
                text:
                    response.data.response === "SUCCESS"
                        ? "Badge saved successfully"
                        : response.data.response,
            });

            if (response.data.response === "SUCCESS") {
                fetchBadges();
                document.getElementById("closeBadgeModal").click();
                resetForm();
            }
        } catch (error) {
            console.error("Error saving badge:", error);
            Swal.fire("Error", "Failed to save badge", "error");
        } finally {
            updateState({ formSubmitting: false });
        }
    };


    const handleFormSubmitIfEmpty = () => {
        if (
            state.formData.badgesName === "" ||
            state.formData.badgesName === null ||
            state.formData.badgesCategory === "" ||
            state.formData.badgesCategory === null ||
            state.formData.badgesCriteriaEarning === "" ||
            state.formData.badgesCriteriaEarning === null ||
            state.formData.badgesPoint === "" ||
            state.formData.badgesPoint === null
        ) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Fields May Not be Empty",
            });
        } else {
            handleFormSubmit();
        }
    };

    // Filter and pagination
    // changes done by sudeep 5/6/2025
    const filteredBadges = state.badges.filter((item) => {
        const matchesSearch =
            (item.badgesName || "")
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            (item.badgesCategory || "")
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            (item.badgesCriteriaEarning || "")
                .toLowerCase()
                .includes(state.searchQuery.toLowerCase());

        const matchesStatus =
            state.filterStatus === "all" ||
            (state.filterStatus === "active" && item.badgeStatus) ||
            (state.filterStatus === "inactive" && !item.badgeStatus);

        return matchesSearch && matchesStatus;
    });

    const pageCount = Math.ceil(filteredBadges.length / state.itemsPerPage);
    const displayedBadges = filteredBadges.slice(
        state.currentPage * state.itemsPerPage,
        (state.currentPage + 1) * state.itemsPerPage
    );

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            Badge Management
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by Name, Category or Criteria"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filter */}
                                            {/* <div className="d-flex align-items-center">
                                                <i className="fas fa-filter me-2 text-muted"></i>
                                                <select
                                                    className="form-select"
                                                    value={state.filterStatus}
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                    style={{
                                                        minWidth: "120px",
                                                    }}
                                                >
                                                    <option value="all">
                                                        All Status
                                                    </option>
                                                    <option value="active">
                                                        Active
                                                    </option>
                                                    <option value="inactive">
                                                        Inactive
                                                    </option>
                                                </select>
                                            </div> */}

                                            {/* Add Badge Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#badgeModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Badge
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Badge Table */}
                                    <div className="table-responsive">
                                        {state.loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <ClipLoader
                                                    color="#4e73df"
                                                    size={50}
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading badges...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="10%">
                                                                ID
                                                            </th>
                                                            <th width="20%">
                                                                Name
                                                            </th>
                                                            <th width="20%">
                                                                Category
                                                            </th>
                                                            <th width="25%">
                                                                Earning Criteria
                                                            </th>
                                                            <th width="10%">
                                                                Points
                                                            </th>
                                                            {/* <th width="10%">
                                                                Status
                                                            </th> */}
                                                            <th
                                                                width="10%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedBadges.length >
                                                        0 ? (
                                                            displayedBadges.map(
                                                                (item) => (
                                                                    <tr
                                                                        key={
                                                                            item.badgesId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                item.badgesId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.badgesName
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.badgesCategory
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.badgesCriteriaEarning
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.badgesPoint
                                                                            }
                                                                        </td>
                                                                        {/* <td>
                                                                            <span
                                                                                style={{
                                                                                    color: item.badgeStatus
                                                                                        ? "red"
                                                                                        : "green",
                                                                                }}
                                                                            >
                                                                                {item.badgeStatus
                                                                                    ? "Inactive"
                                                                                    : "Active"}
                                                                            </span>
                                                                        </td> */}
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#badgeModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            item
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() =>
                                                                                        handleDelete(
                                                                                            item.badgesId
                                                                                        )
                                                                                    }
                                                                                    title="Delete"
                                                                                >
                                                                                    <i className="fas fa-trash"></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="7"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No badges"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No
                                                                            badges
                                                                            found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#badgeModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add
                                                                            New
                                                                            Badge
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedBadges.length > 0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={
                                                            state.currentPage
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Badge Modal */}
            <div
                className="modal fade"
                id="badgeModal"
                tabIndex="-1"
                aria-labelledby="badgeModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="badgeModalLabel">
                                {state.formData.badgesId > 0
                                    ? "Edit Badge"
                                    : "Add New Badge"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="badgesName"
                                        className="form-label required"
                                    >
                                        Badge Name
                                    </label>
                                    <div className="col-sm-9">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="badgesName"
                                        name="badgesName"
                                        value={state.formData.badgesName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                </div>
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="badgesCategory"
                                        className="form-label required"
                                    >
                                        Category
                                    </label>
                                    <div className="col-sm-9">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="badgesCategory"
                                        name="badgesCategory"
                                        value={state.formData.badgesCategory}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                </div>
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="badgesCriteriaEarning"
                                        className="form-label required"
                                    >
                                        Earning Criteria
                                    </label>
                                    <div className="col-sm-9">
                                    <textarea
                                        className="form-control"
                                        id="badgesCriteriaEarning"
                                        name="badgesCriteriaEarning"
                                        rows="1"
                                        value={
                                            state.formData.badgesCriteriaEarning
                                        }
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                </div>
                                <div className="mb-1 row">
                                    <label
                                        htmlFor="badgesPoint"
                                        className="form-label required"
                                    >
                                        Points
                                    </label>
                                    <div className="col-sm-9">
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="badgesPoint"
                                        name="badgesPoint"
                                        value={state.formData.badgesPoint}
                                        onChange={handleInputChange}
                                        min="0"
                                        required
                                    />
                                </div>
                                </div>
                                {/* {state.formData.badgesId > 0 && (
                                    <div className="mb-3">
                                        <label
                                            htmlFor="badgeStatus"
                                            className="form-label"
                                        >
                                            Status
                                        </label>
                                        <select
                                            className="form-select"
                                            id="badgeStatus"
                                            name="badgeStatus"
                                            value={state.formData.badgeStatus}
                                            onChange={handleInputChange}
                                        >
                                            <option value={true}>Active</option>
                                            <option value={false}>
                                                Inactive
                                            </option>
                                        </select>
                                    </div>
                                )} */}
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeBadgeModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                // onClick={handleFormSubmit}
                                onClick={handleFormSubmitIfEmpty}
                                disabled={state.formSubmitting}
                            >
                                {state.formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : (
                                    "Save Changes"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
